import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './components/SEO';
import { getOrganizationSchema } from './components/CompetitionSchema';

export default function About() {
  // SEO Data for About page
  const seoData = {
    title: "About us!",
    description: "Discover Capstone Pakistan's mission to empower Pakistan's brightest STEM talents through innovative competitions, international partnerships, and academic excellence programs.",
    keywords: "Capstone Pakistan about, mission vision, STEM education Pakistan, academic competitions, mathematical modeling, international partnerships, Pakistan education, student development",
    url: "https://capstonepakistan.org/about",
    structuredData: getOrganizationSchema()
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="min-h-screen bg-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="fixed inset-0 pointer-events-none">
          {/* Floating geometric shapes */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-10 animate-bounce" style={{animationDuration: '6s'}}></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-10 animate-pulse" style={{animationDuration: '4s'}}></div>
          <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full opacity-10 animate-bounce" style={{animationDuration: '8s'}}></div>
          <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full opacity-10 animate-pulse" style={{animationDuration: '5s'}}></div>
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
          
          {/* Gradient mesh */}
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-purple-50 opacity-60"></div>
        </div>
        {/* Hero Section - matches navbar color */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                About Capstone
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-4xl mx-auto">
                Discovering and nurturing Pakistan's brightest talents
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                Our Mission & Vision
              </h2>
              <p className="text-xl lg:text-2xl text-gray-900 leading-relaxed max-w-3xl mx-auto">
                Empowering the next generation of Pakistani innovators and problem solvers
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Mission Card */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-center">
                  Empowering students to reach their full potential through engaging and challenging competitions. We strive to create opportunities that inspire academic excellence and foster a culture of innovation and collaboration.
                </p>
              </div>
              
              {/* Values Card */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-center">
                  Integrity, Excellence, Collaboration, and Innovation in education. We believe in fair competition, transparent processes, and celebrating every achievement along the journey.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                What We Do
              </h2>
              <p className="text-xl lg:text-2xl text-gray-900 leading-relaxed max-w-3xl mx-auto">
                Comprehensive programs designed to identify, nurture, and showcase Pakistan's brightest talents
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Organize Competitions */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Organize Competitions</h3>
                <p className="text-gray-700 leading-relaxed">
                  We host national competitions that serve as qualifying stages for international events, giving Pakistani students a platform to showcase their talents globally.
                </p>
              </div>
              
              {/* Identify Talent */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Identify Talent</h3>
                <p className="text-gray-700 leading-relaxed">
                  Through rigorous competitions, we identify and nurture the brightest young minds.
                </p>
              </div>
              
              {/* Represent Pakistan */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Represent Pakistan</h3>
                <p className="text-gray-700 leading-relaxed">
                  Selected teams represent Pakistan at international competitions, bringing pride to our nation and showcasing Pakistani talent worldwide.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Competitions Section - Dynamic Design */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Our Competitions
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Three prestigious competitions that open doors to international recognition
              </p>
            </div>
            
            <div className="space-y-6 max-w-4xl mx-auto">
              {/* PMMC */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-4"></div>
                  <h3 className="text-2xl font-bold text-gray-900">Pakistan Mathematical Modeling Challenge (PMMC)</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  The official national competition for the International Mathematical Modeling Challenge (IMMC), focusing on mathematical modeling skills and problem-solving.
                </p>
              </div>
              
              {/* WMTC */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-4"></div>
                  <h3 className="text-2xl font-bold text-gray-900">World Mathematics Team Championship (WMTC)</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  A team-based international mathematics competition that brings together top students from around the world to compete in advanced mathematics.
                </p>
              </div>
              
              {/* PGEO */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-purple-500 rounded-full mr-4"></div>
                  <h3 className="text-2xl font-bold text-gray-900">Pakistan Geography Olympiad (PGEO)</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  The national qualifying competition for the International Geography Olympiad (iGeo), promoting geographic literacy and skills.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Champions Section - Dynamic Design */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Our Champions
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Celebrating the brightest minds who represent Pakistan on the global stage
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/WhatsApp Image 2024-09-27 at 8.03.42 AM.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/WhatsApp Image 2024-09-27 at 8.03.41 AM.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/WhatsApp Image 2024-09-27 at 8.03.40 AM.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/group.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/team picture.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-300">
                  <img src="/WhatsApp Image 2024-09-27 at 8.03.43 AM.png" alt="Winner" className="w-full h-48 object-cover" />
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <a 
                href="https://drive.google.com/file/d/1uBbLNwUNVGLnw3cfmu1eyTmbPWoviu0i/view?usp=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 bg-white hover:bg-gray-100 text-blue-700 font-semibold text-lg rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                View 2024 Student Awards
              </a>
            </div>
          </div>
        </section>

        {/* Advisory Committee Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                Advisory Committee
              </h2>
              <p className="text-xl lg:text-2xl text-gray-900 leading-relaxed max-w-3xl mx-auto">
                Expert guidance from distinguished academics and professionals
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Dr. Basit Ali */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Dr. Basit Ali</h3>
                  <p className="text-blue-600 font-medium">University of Management and Technology</p>
                </div>
              </div>
              
              {/* Dr. Wajiha Riaz */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Dr. Wajiha Riaz</h3>
                  <p className="text-blue-600 font-medium">University of Karachi</p>
                </div>
              </div>
              
              {/* Arif Hussain */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Arif Hussain</h3>
                  <p className="text-blue-600 font-medium">FAST University Islamabad</p>
                </div>
              </div>
              
              {/* Hamza Khurram */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Hamza Khurram</h3>
                  <p className="text-blue-600 font-medium">Pivotal Education</p>
                </div>
              </div>
              
              {/* Hashir Salman */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Hashir Salman</h3>
                  <p className="text-blue-600 font-medium">Lahore University of Management Sciences</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* School Coordinators Section - Dynamic Design */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                School Coordinators
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Dedicated coordinators working across Pakistan to identify and nurture talent
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Row 1 */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Sham Kumar</h3>
                  <p className="text-blue-600 font-medium">Foundation Public School</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Murtaza Ahmed</h3>
                  <p className="text-blue-600 font-medium">The City School</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Muhammad Huzaifa</h3>
                  <p className="text-blue-600 font-medium">Beaconhouse</p>
                </div>
              </div>
              
              {/* Row 2 */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Shaheera Kashif</h3>
                  <p className="text-blue-600 font-medium">FAST NUCES University</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unaiza Afzal</h3>
                  <p className="text-blue-600 font-medium">Government College of Science</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Umaan Ubaid</h3>
                  <p className="text-blue-600 font-medium">City School</p>
                </div>
              </div>
              
              {/* Row 3 */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Muhammad Aadil Chaudhary</h3>
                  <p className="text-blue-600 font-medium">International School Lahore</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fajar Amjad</h3>
                  <p className="text-blue-600 font-medium">Beaconhouse</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fatima Noor Jawad</h3>
                  <p className="text-blue-600 font-medium">Beaconhouse</p>
                </div>
              </div>
              
              {/* Row 4 */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Burhan Abeed</h3>
                  <p className="text-blue-600 font-medium">BSS JTC</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Mamoona Raza</h3>
                  <p className="text-blue-600 font-medium">Froebel's International School</p>
                </div>
              </div>
              
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fatima Nisar</h3>
                  <p className="text-blue-600 font-medium">Punjab College</p>
                </div>
              </div>
              
              {/* Row 5 */}
              <div className="bg-white rounded-[20px] p-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Mehwish Khan</h3>
                  <p className="text-blue-600 font-medium">APSACS</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                Our Impact
              </h2>
              <p className="text-xl lg:text-2xl text-gray-900 leading-relaxed max-w-3xl mx-auto">
                Transforming lives and building a stronger academic community
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* For Students */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">For Students</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Opportunity to compete at international levels</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Recognition and certificates for achievements</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Networking with like-minded peers</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Enhanced problem-solving and critical thinking skills</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Potential scholarships and career opportunities</span>
                  </div>
                </div>
              </div>
              
              {/* For Pakistan */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">For Pakistan</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Representation at international competitions</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Showcasing Pakistani talent globally</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Building a stronger academic community</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Promoting STEM education nationwide</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700">Creating role models for future generations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Get Involved Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
              Get Involved
            </h2>
            <p className="text-xl lg:text-2xl text-gray-900 leading-relaxed max-w-3xl mx-auto mb-12">
              Join us in our mission to discover and nurture Pakistan's brightest talents!
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <Link 
                to="/#our-assessments" 
                className="inline-block px-8 py-4 bg-black hover:bg-gray-800 text-white font-semibold text-lg rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Register for Competitions
              </Link>
              <a 
                href="mailto:admin@capstonepakistan.org" 
                className="inline-block px-8 py-4 bg-transparent hover:bg-gray-100 text-black font-semibold text-lg rounded-lg transition-colors duration-200 border-2 border-black"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
} 